import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';
import { toCalendarDate, formatCalendarDate } from '../../../utils/UtilFunctions';

interface IImportantDate {
  id: number;
  type: string;
  title: string;
  date: string;
  recurring: boolean;
  notes: string | null;
  client: { id: number; name: string };
}

interface Props {
  data: IImportantDate[];
  loading: boolean;
  onAdd?: () => void;
}

const TYPE_ICONS: Record<string, string> = {
  birthday: 'pi pi-gift',
  anniversary: 'pi pi-heart',
  surgery: 'pi pi-shield',
  other: 'pi pi-star'
};

const TYPE_COLORS: Record<string, string> = {
  birthday: '#ec4899',
  anniversary: '#4d7c0f',
  surgery: '#f97316',
  other: '#8b5cf6'
};

export const ImportantDatesWidget: React.FC<Props> = ({ data, loading, onAdd }) => {
  const intl = useIntl();

  // #38 ítem 1: las fechas importantes son días-calendario guardados a medianoche UTC.
  const formatDate = (dateStr: string) => formatCalendarDate(dateStr, intl.locale, { day: 'numeric', month: 'short' });

  const getDaysUntil = (dateStr: string) => {
    const d = toCalendarDate(dateStr);
    if (!d) return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((d.getTime() - today.getTime()) / (1000 * 3600 * 24));
  };

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.importantDates' })}
      icon="pi pi-star"
      iconColor="#8b5cf6"
      loading={loading}
      onViewAll={onAdd}
      viewAllLabel="+"
    >
      {data.length === 0 ? (
        <p style={{ color: 'var(--ios-text-tertiary)', fontSize: '0.85rem', textAlign: 'center', padding: '1rem 0' }}>
          {intl.formatMessage({ id: 'dashboard.noImportantDates' })}
        </p>
      ) : (
        <div className="flex flex-column gap-2">
          {data.slice(0, 5).map((item) => {
            const daysUntil = getDaysUntil(item.date);
            return (
              <div
                key={item.id}
                className="flex align-items-center gap-2"
                style={{
                  padding: '0.5rem',
                  borderRadius: 'var(--ios-radius-md)',
                  background: 'var(--ios-surface-subtle)'
                }}
              >
                <i
                  className={TYPE_ICONS[item.type] ?? 'pi pi-star'}
                  style={{
                    color: TYPE_COLORS[item.type] ?? '#8b5cf6',
                    fontSize: '0.85rem',
                    width: '1.2rem',
                    textAlign: 'center'
                  }}
                />
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      color: 'var(--ios-text)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {item.title}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ios-text-tertiary)' }}>{item.client.name}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: TYPE_COLORS[item.type] ?? '#8b5cf6' }}>
                    {formatDate(item.date)}
                  </div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--ios-text-tertiary)' }}>
                    {daysUntil === 0
                      ? intl.formatMessage({ id: 'dashboard.today' })
                      : daysUntil === 1
                        ? intl.formatMessage({ id: 'dashboard.tomorrow' })
                        : `${daysUntil}d`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardWidget>
  );
};
